import { tContactInfo,
  tProductDetailItem,
} from '.';
import { zDigitalOrderResponse } from './api';
import { zOrder } from './application';
import { zSafeParse } from './helpers';
import {
  tCarrierSelected,
  tDigitalOrderResponse,
  tImage,
  tOrder,
  tOrderLine,
  tPickupContactInfo,
  tReturnPostObject,
  tSingleOrderFromApi,
  tSummaryOrderLines,
  tSummaryPostObject,
  tSummaryResponse,
  tUpdateAndAddToReturnArgs,
} from './types';
import { ReturnFormsAnswer } from '../../hooks/useReturnForms';

export const orderTransformer = (orders: tDigitalOrderResponse): tOrder[] => {
  const result = zSafeParse(zDigitalOrderResponse, orders);

  const applicationOrders: tOrder[] = result?.orders?.map(
    (order: tSingleOrderFromApi) => ({
      customerNumber: order.customerNumber,
      orderNumber: order.orderNumber,
      orderDate: order.orderDate,
      orderNumberAliases: order.orderNumberAliases,
      orderLines: order.orderLines.map((item) => {
        const orderLine: tOrderLine = {
          uid: item.uid,
          description: item.product.description,
          imageUrl: item.product.imageUrl,
          productNumber: item.product.productNumber,
          orderNumber: order.orderNumber,
          salesOrderLineId: item.salesOrderLineId,
          labelTranslationKey: item.labelTranslationKey ?? '',
          variant: item.product.variant,
          variantDescription: item.product.variantDescription,
          quantity: item.quantity,
          shippedQuantity: item.shippedQuantity,
          mayBeReturned: item.mayBeReturned,
          mayBeClaimed: item.mayBeClaimed,
        };

        return orderLine;
      }),
    }),
  );

  const transformedOrders = zSafeParse(zOrder.array(), applicationOrders);
  return transformedOrders;
};

export const productDetailTransformer = (products: tProductDetailItem[]) => {
  const applicationProducts = products.map((product: tProductDetailItem) => ({
    imageUrl: product.imageUrl,
    productNumber: product.productNumber,
    productDescription: product.productDescription,
    variantDescription: product.variantDescription,
    variant: product.variant,
    orderLines: product.orderLines.map((item) => {
      const orderLine: tOrderLine = {
        salesOrderLineId: item.salesOrderLineId,
        orderNumber: item.orderNumber,
        productNumber: product.productNumber,
        description: product.productDescription,
        variant: product.variant,
        variantDescription: product.variantDescription,
        imageUrl: product.imageUrl,
        orderDate: item.orderDate,
        quantity: item.quantity,
        shippedQuantity: item.shippedQuantity,
        mayBeReturned: item.mayBeReturned,
        mayBeClaimed: item.mayBeClaimed,
        labelTranslationKey: item.labelTranslationKey,
        uid: `${item.orderNumber}-${item.salesOrderLineId.toString()}`,
      };

      return orderLine;
    }),
  }));

  return applicationProducts[0];
};

interface IsummaryTranformer {
  returns: tUpdateAndAddToReturnArgs[];
  carrier: tCarrierSelected;
}

export const summaryTransformer = ({
  returns,
  carrier,
}: IsummaryTranformer): tSummaryPostObject => {
  const uniqueReturnsOrderNos = [
    ...new Set(returns.map((item) => item.orderNumber)),
  ];
  const returnOrders = [];
  uniqueReturnsOrderNos.forEach((orderNumber) => {
    const currentReturns = returns.filter(
      (item) => item.orderNumber === orderNumber,
    );
    const summaryOrderLines: tSummaryOrderLines[] = currentReturns.map(
      (returnItem: tUpdateAndAddToReturnArgs) => ({
        salesOrderLineId: returnItem.salesOrderLineId,
        returnedQuantity: returnItem.returnData.amount,
        comment: returnItem.returnData.returnDescription,
        reasonCode: returnItem.returnData.reason,
        imageReferences: null,
        exchange: null,
      }),
    );
    returnOrders.push({
      orderNumber,
      returnFulfilmentType: 'None',
      returnOrderLines: summaryOrderLines,
    });
  });
  const result: tSummaryPostObject = {
    returnOrders,
    chosenCarrierService: carrier.code,
  };
  return result;
};

export const returnTransformer = (
  languageCode: string,
  summary: tSummaryResponse,
  pickupContact: tPickupContactInfo,
  createShippingContact: boolean,
  shippingContact: tContactInfo | undefined | null,
  numberOfParcels: number,
  images: tImage[],
  defaultCustomerNumber: string,
  returnFormsAnswers: ReturnFormsAnswer[],
  returns: tUpdateAndAddToReturnArgs[],
) => {
  const returnOrders = summary.returnOrders.map((order) => ({
    ...order,
    returnOrderLines: order.returnOrderLines.map((line) => ({
      ...line,
      imageReferences: images.filter((image) =>
        image.salesOrderLineId === line.salesOrderLineId && image.reason === line.reasonCodes.code,
      ).map((filteredImage) => filteredImage.reference),
      returnFormsAnswers: returnFormsAnswers.filter((formAnswer) => (
        formAnswer.salesOrderLineId === line.salesOrderLineId && formAnswer.reason === line.reasonCodes.code
      )).map((formAnswer) => ({
        returnFormComponentId: formAnswer.returnFormComponentId,
        answer: formAnswer.answer,
      })),
      reasonQuestionAnswers: returns.find((returnItem) => {
        return returnItem.salesOrderLineId === line.salesOrderLineId && returnItem.returnData.reason === line.reasonCodes.code;
      })?.returnData.reasonQuestionAnswers ?? [],
    })),
  }));

  const result: tReturnPostObject = {
    iso6391: languageCode,
    customerNumber: defaultCustomerNumber,
    returnOrders,
    numberOfParcels,
    chosenCarrierService: summary.chosenCarrierService,
    summaryResult: summary.summaryResult,
    pickupContact,
    createShippingContact,
    shippingContact,
  };

  return result;
};
